import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { getRequestEvaluations } from 'lib/api';
import { buildRequestId } from '../utils';
import RiskCard from './RiskCard';
import RiskCardSkeleton from './RiskCardSkeleton';
import EvaluationHistoryDialog from './EvaluationHistoryDialog';
import AlertBanner from 'lib/designSystem/AlertBanner';
import { Assessment } from '@mui/icons-material';

interface RiskEvaluation {
  identifier: string;
  evaluation: string;
  evaluations: any[];
}

interface RiskReviewsProps {
  item: any;
  requestTypes: string[];
}

const RiskReviews: React.FC<RiskReviewsProps> = ({ item, requestTypes }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [riskDataArray, setRiskDataArray] = useState<RiskEvaluation[]>([]);
  const [selectedRiskData, setSelectedRiskData] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const getRiskEvaluations = async () => {
    setLoading(true);
    try {
      const promises = requestTypes.map(async (requestType) => {
        try {
          const response = await getRequestEvaluations(dispatch, {
            requestId: buildRequestId(item, requestType, 'quote'),
            requestType,
          });
          return response;
        } catch (error) {
          console.log(`Error fetching ${requestType}:`, error);
          return null;
        }
      });

      const results = await Promise.all(promises);
      const validResults = results.filter((result) => result !== null).flat();

      setRiskDataArray(validResults);
    } catch (error) {
      console.log('Error fetching risk evaluations:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (item && requestTypes.length > 0) {
      getRiskEvaluations();
    }
  }, [item, requestTypes]);

  const handleViewHistory = (riskData: any) => {
    setSelectedRiskData(riskData);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRiskData(null);
  };

  if (loading)
    return (
      <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
        {[1, 2].map((index) => (
          <RiskCardSkeleton key={index} />
        ))}
      </Box>
    );

  if (riskDataArray.length === 0)
    return (
      <AlertBanner
        severity="warning"
        icon={<Assessment sx={{ fontSize: '1.5rem', color: 'warning.main' }} />}
        title="Motor de riesgo no ejecutado"
        message="Para obtener una evaluación completa del riesgo, es necesario ejecutar el motor de decisión automatizado. Este proceso analizará múltiples factores de riesgo y proporcionará una evaluación detallada."
        description="Una vez ejecutado, podrás ver el historial completo de evaluaciones y los detalles de cada análisis de riesgo."
      />
    );

  return (
    <Box sx={{ my: 2, display: 'flex', flexDirection: 'column' }}>
      {riskDataArray.map((riskData, riskIndex) => (
        <RiskCard
          key={riskIndex}
          riskData={riskData}
          onViewHistory={() => handleViewHistory(riskData)}
        />
      ))}

      <EvaluationHistoryDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        riskData={selectedRiskData}
      />
    </Box>
  );
};

export default RiskReviews;
